import type {
  GithubCommits,
  GithubPullRequest,
  GithubTimelineItem,
  IssueComments,
  ReviewComments,
  Reviews,
} from '@/types/github';

export const TIMELINE_ITEM_TYPE = {
  Commit: 'commit',
  Review: 'review',
  IssueComment: 'issue_comment',
  ReviewComment: 'review_comment',
  MergeCommit: 'merge_commit',
  OtherTimelineItem: 'other_timeline_item',
} as const;
export type TimelineItemType = (typeof TIMELINE_ITEM_TYPE)[keyof typeof TIMELINE_ITEM_TYPE];

export type User = {
  login: string;
  avatar_url: string;
  html_url: string;
};

export type BaseTimelineItem = {
  id: string;
  timestamp: string;
  user: User;
  content?: string;
  parentId?: string;
  childrenIds?: string[];
  optimisticReplyIds?: string[];
  node_id: string;
};

export type CommitTimelineItem = BaseTimelineItem &
  Omit<GithubCommits[number], 'id' | 'user'> & {
    type: typeof TIMELINE_ITEM_TYPE.Commit;
    isPending?: boolean;
  };

export type ReviewTimelineItem = BaseTimelineItem &
  Omit<Reviews[number], 'id' | 'user'> & {
    type: typeof TIMELINE_ITEM_TYPE.Review;
    isPending?: boolean;
    url?: string;
    created_at?: string;
    updated_at?: string;
  };

export type ReviewCommentTimelineItem = BaseTimelineItem &
  Omit<ReviewComments[number], 'id' | 'user'> & {
    type: typeof TIMELINE_ITEM_TYPE.ReviewComment;
    isPending?: boolean;
    parentReviewId?: string;
  };

export type IssueCommentTimelineItem = BaseTimelineItem &
  Omit<IssueComments[number], 'id' | 'user'> & {
    type: typeof TIMELINE_ITEM_TYPE.IssueComment;
    isPending?: boolean;
  };

export type MergeCommitTimelineItem = BaseTimelineItem &
  Omit<GithubPullRequest, 'id' | 'user'> & {
    type: typeof TIMELINE_ITEM_TYPE.MergeCommit;
    isPending?: boolean;
  };

export type OtherTimelineItem = GithubTimelineItem &
  BaseTimelineItem & {
    type: typeof TIMELINE_ITEM_TYPE.OtherTimelineItem;
  };

export type TimelineItem =
  | CommitTimelineItem
  | ReviewTimelineItem
  | ReviewCommentTimelineItem
  | IssueCommentTimelineItem
  | MergeCommitTimelineItem
  | OtherTimelineItem;
